import React, { useState, useEffect, useRef } from 'react';
import { Card } from './ui/card';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import { StarsBackground } from './ui/stars-background';
import { motion } from 'framer-motion';
import { fadeIn } from '../utils/motion';
import { FileText, MessageSquare, Award, TrendingUp, Users } from 'lucide-react';
import { PdfDownloadButton } from './ui/pdf-download-button';

interface InterviewAnalysisProps {
  markdown: string;
  overallScore?: number;
}

// Custom components for ReactMarkdown to style links
const MarkdownComponents: Components = {
  a: ({ href, children, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
    <a 
      href={href}
      {...props} 
      className="text-blue-600 hover:text-blue-800 underline cursor-pointer transition-colors"
      target="_blank" 
      rel="noopener noreferrer"
      style={{ fontSize: '16px' }}
    >
      {children}
    </a>
  ),
  p: ({ children, ...props }) => (
    <p {...props} style={{ fontSize: '16px', fontFamily: '"Open Sans", sans-serif' }}>
      {children}
    </p>
  ),
  li: ({ children, ...props }) => (
    <li {...props} style={{ fontSize: '16px', fontFamily: '"Open Sans", sans-serif' }}>
      {children}
    </li>
  )
};

// Helper function to clean up markdown content
const cleanMarkdown = (markdown: string): string => {
  if (!markdown) return '';
  
  try {
    // Replace patterns like [https://example.com](https://example.com) with just https://example.com
    let cleaned = markdown.replace(/\[(\bhttps?:\/\/[^\s\]]+)\]\(\1\)/g, '$1');
    
    // Remove patterns like [url](url) that appear after "Source: url"
    cleaned = cleaned.replace(/(Source:.*?)(\s*\[.*?\]\(.*?\))/gi, '$1');
    
    // Remove any standalone ** at the beginning of the text
    cleaned = cleaned.replace(/^\s*\*\*\s*/g, '');
    
    // Remove any standalone ** at the end of the text
    cleaned = cleaned.replace(/\s*\*\*\s*$/g, '');
    
    return cleaned;
  } catch (error) {
    console.error('Error in cleanMarkdown:', error);
    return markdown;
  }
};

const InterviewAnalysis: React.FC<InterviewAnalysisProps> = ({ markdown, overallScore }) => {
  if (!markdown) return null;
  
  // State to track animation and loading state
  const [isVisible, setIsVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Ref for the content container to be captured in PDF
  const contentRef = useRef<HTMLDivElement>(null);
  
  // Parse markdown to identify sections
  const sections = parseMarkdownSections(markdown);
  
  // Determine performance label and colors based on score
  const getPerformanceInfo = () => {
    if (overallScore === undefined || overallScore === null) return null;
    
    if (overallScore >= 8) {
      return { 
        text: 'Excellent Performance', 
        bannerColor: 'bg-green-100 text-green-800 border-green-300',
        headerBgColor: 'bg-green-50',
        headerBorderColor: 'border-green-200',
        iconColor: 'text-green-500',
        sectionHeaderColor: 'text-green-900'
      };
    } else if (overallScore >= 6) {
      return { 
        text: 'Good Performance', 
        bannerColor: 'bg-blue-100 text-blue-800 border-blue-300',
        headerBgColor: 'bg-blue-50',
        headerBorderColor: 'border-blue-200',
        iconColor: 'text-blue-500',
        sectionHeaderColor: 'text-blue-900'
      };
    } else if (overallScore >= 4) {
      return { 
        text: 'Average Performance', 
        bannerColor: 'bg-yellow-100 text-yellow-800 border-yellow-300',
        headerBgColor: 'bg-yellow-50',
        headerBorderColor: 'border-yellow-200',
        iconColor: 'text-yellow-500',
        sectionHeaderColor: 'text-yellow-900'
      };
    } else if (overallScore >= 2) {
      return { 
        text: 'Poor Performance', 
        bannerColor: 'bg-orange-100 text-orange-800 border-orange-300',
        headerBgColor: 'bg-orange-50',
        headerBorderColor: 'border-orange-200',
        iconColor: 'text-orange-500',
        sectionHeaderColor: 'text-orange-900'
      };
    } else {
      return { 
        text: 'Critical Performance Issues', 
        bannerColor: 'bg-red-100 text-red-800 border-red-300',
        headerBgColor: 'bg-red-50',
        headerBorderColor: 'border-red-200',
        iconColor: 'text-red-500',
        sectionHeaderColor: 'text-red-900'
      };
    }
  };
  
  const performanceInfo = getPerformanceInfo();
  
  // Animation effect on mount
  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Handle PDF generation errors
  const handlePdfError = (error: Error) => {
    console.error('Error generating PDF:', error);
  };

  return (
    <div className={`space-y-8 transition-opacity duration-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`} ref={contentRef}>
      {/* Header with Download Button */}
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Interview Analysis</h1>
        <PdfDownloadButton
          contentRef={contentRef}
          fileName="Interview_Analysis"
          documentTitle="Interview Analysis"
          documentSubject="VC Interview Analysis and Feedback"
          loading={loading}
          setLoading={setLoading}
          onError={handlePdfError}
          position="right"
        />
      </div>
      
      {/* Performance Label */}
      {performanceInfo && (
        <motion.div
          variants={fadeIn("up", "spring", 0.1, 0.75)}
          initial="hidden"
          animate="show"
          className="w-full"
        >
          <div className={`flex items-center justify-between px-6 py-3 rounded-lg ${performanceInfo.bannerColor} border shadow-sm mb-6`} style={{ fontFamily: '"Open Sans", sans-serif' }}>
            <div className="flex items-center">
              {overallScore && (overallScore >= 6 ? (
                <Award className={`mr-2 ${performanceInfo.iconColor}`} size={24} />
              ) : overallScore >= 4 ? (
                <TrendingUp className={`mr-2 ${performanceInfo.iconColor}`} size={24} />
              ) : (
                <MessageSquare className={`mr-2 ${performanceInfo.iconColor}`} size={24} />
              ))}
              <span className="font-semibold text-lg">{performanceInfo.text}</span>
            </div>
            <div className="text-right">
              <span className="font-bold text-lg">Score: {overallScore?.toFixed(1)}/10</span>
            </div>
          </div>
        </motion.div>
      )}
      {/* Main content with enhanced visual structure */}
      {sections.length > 0 ? (
        <div className="space-y-6">
          {/* Executive Summary Section */}
          {sections.find(s => s.id === 'executive-summary' || s.id === 'overview') && (
            <motion.div
              variants={fadeIn("up", "spring", 0.3, 0.75)}
              initial="hidden"
              animate="show"
              className="w-full"
            >
              <Card className="p-6 relative bg-transparent border border-gray-200 shadow-2xl backdrop-blur-sm rounded-xl mb-6">
                <div className="absolute inset-0 -z-10 overflow-hidden rounded-xl">
                  <StarsBackground />
                </div>
                <div className="relative z-10">
                  <div className={`px-6 py-4 border-b ${performanceInfo ? performanceInfo.headerBorderColor : 'border-gray-300'} ${performanceInfo ? performanceInfo.headerBgColor : 'bg-transparent'}`}>
                    <h2 className={`text-2xl font-bold text-left ${performanceInfo ? performanceInfo.sectionHeaderColor : 'text-gray-900'} flex items-center`} style={{ fontFamily: '"Open Sans", sans-serif' }}>
                      <FileText className={`mr-2 ${performanceInfo ? performanceInfo.iconColor : 'text-blue-500'}`} size={24} />
                      Executive Summary
                    </h2>
                  </div>
                  <div className="p-6 text-left text-gray-700 prose prose-slate max-w-none" style={{ fontFamily: '"Open Sans", sans-serif', fontSize: '16px' }}>
                    <ReactMarkdown components={MarkdownComponents}>
                      {cleanMarkdown(sections.find(s => s.id === 'executive-summary' || s.id === 'overview')?.content || '')}
                    </ReactMarkdown>
                  </div>
                </div>
              </Card>
            </motion.div>
          )}

          {/* Interview Performance Section */}
          {sections.find(s => s.id.includes('performance') || s.id.includes('assessment')) && (
            <motion.div
              variants={fadeIn("up", "spring", 0.4, 0.75)}
              initial="hidden"
              animate="show"
              className="w-full"
            >
              <Card className="p-6 relative bg-transparent border border-gray-200 shadow-2xl backdrop-blur-sm rounded-xl mb-6">
                <div className="absolute inset-0 -z-10 overflow-hidden rounded-xl">
                  <StarsBackground />
                </div>
                <div className="relative z-10">
                  <div className={`px-6 py-4 border-b ${performanceInfo ? performanceInfo.headerBorderColor : 'border-gray-300'} ${performanceInfo ? performanceInfo.headerBgColor : 'bg-transparent'}`}>
                    <h2 className={`text-2xl font-bold text-left ${performanceInfo ? performanceInfo.sectionHeaderColor : 'text-gray-900'} flex items-center`} style={{ fontFamily: '"Open Sans", sans-serif' }}>
                      <Award className={`mr-2 ${performanceInfo ? performanceInfo.iconColor : 'text-blue-500'}`} size={24} />
                      Performance Assessment
                    </h2>
                  </div>
                  <div className="p-6 text-left text-gray-700 prose prose-slate max-w-none" style={{ fontFamily: '"Open Sans", sans-serif', fontSize: '16px' }}>
                    <ReactMarkdown components={MarkdownComponents}>
                      {cleanMarkdown(sections.find(s => s.id.includes('performance') || s.id.includes('assessment'))?.content || '')}
                    </ReactMarkdown>
                  </div>
                </div>
              </Card>
            </motion.div>
          )}

          {/* Key Insights Section */}
          {sections.find(s => s.id.includes('insight') || s.id.includes('key-point')) && (
            <motion.div
              variants={fadeIn("up", "spring", 0.5, 0.75)}
              initial="hidden"
              animate="show"
              className="w-full"
            >
              <Card className="p-6 relative bg-transparent border border-gray-200 shadow-2xl backdrop-blur-sm rounded-xl mb-6">
                <div className="absolute inset-0 -z-10 overflow-hidden rounded-xl">
                  <StarsBackground />
                </div>
                <div className="relative z-10">
                  <div className={`px-6 py-4 border-b ${performanceInfo ? performanceInfo.headerBorderColor : 'border-gray-300'} ${performanceInfo ? performanceInfo.headerBgColor : 'bg-transparent'}`}>
                    <h2 className={`text-2xl font-bold text-left ${performanceInfo ? performanceInfo.sectionHeaderColor : 'text-gray-900'} flex items-center`} style={{ fontFamily: '"Open Sans", sans-serif' }}>
                      <TrendingUp className={`mr-2 ${performanceInfo ? performanceInfo.iconColor : 'text-blue-500'}`} size={24} />
                      Key Insights
                    </h2>
                  </div>
                  <div className="p-6 text-left text-gray-700 prose prose-slate max-w-none" style={{ fontFamily: '"Open Sans", sans-serif', fontSize: '16px' }}>
                    <ReactMarkdown components={MarkdownComponents}>
                      {cleanMarkdown(sections.find(s => s.id.includes('insight') || s.id.includes('key-point'))?.content || '')}
                    </ReactMarkdown>
                  </div>
                </div>
              </Card>
            </motion.div>
          )}

          {/* Recommendations Section */}
          {sections.find(s => s.id.includes('recommend') || s.id.includes('improvement')) && (
            <motion.div
              variants={fadeIn("up", "spring", 0.6, 0.75)}
              initial="hidden"
              animate="show"
              className="w-full"
            >
              <Card className="p-6 relative bg-transparent border border-gray-200 shadow-2xl backdrop-blur-sm rounded-xl mb-6">
                <div className="absolute inset-0 -z-10 overflow-hidden rounded-xl">
                  <StarsBackground />
                </div>
                <div className="relative z-10">
                  <div className={`px-6 py-4 border-b ${performanceInfo ? performanceInfo.headerBorderColor : 'border-gray-300'} ${performanceInfo ? performanceInfo.headerBgColor : 'bg-transparent'}`}>
                    <h2 className={`text-2xl font-bold text-left ${performanceInfo ? performanceInfo.sectionHeaderColor : 'text-gray-900'} flex items-center`} style={{ fontFamily: '"Open Sans", sans-serif' }}>
                      <MessageSquare className={`mr-2 ${performanceInfo ? performanceInfo.iconColor : 'text-blue-500'}`} size={24} />
                      Recommendations
                    </h2>
                  </div>
                  <div className="p-6 text-left text-gray-700 prose prose-slate max-w-none" style={{ fontFamily: '"Open Sans", sans-serif', fontSize: '16px' }}>
                    <ReactMarkdown components={MarkdownComponents}>
                      {cleanMarkdown(sections.find(s => s.id.includes('recommend') || s.id.includes('improvement'))?.content || '')}
                    </ReactMarkdown>
                  </div>
                </div>
              </Card>
            </motion.div>
          )}

          {/* All Other Sections - Each in its own card */}
          {sections
            .filter(section => 
              !section.id.includes('executive-summary') && 
              !section.id.includes('overview') &&
              !section.id.includes('performance') &&
              !section.id.includes('assessment') &&
              !section.id.includes('insight') &&
              !section.id.includes('key-point') &&
              !section.id.includes('recommend') &&
              !section.id.includes('improvement')
            )
            .map((section, index) => {
              // Choose an icon based on the section title or ID
              let SectionIcon = Users;
              if (section.id.includes('pitch-quality') || section.id === 'section-1') {
                SectionIcon = Award;
              } else if (section.id.includes('value') || section.id.includes('proposition')) {
                SectionIcon = TrendingUp;
              } else if (section.id.includes('structure') || section.id.includes('tone')) {
                SectionIcon = FileText;
              } else if (section.id.includes('recommend') || section.id.includes('improvement')) {
                SectionIcon = MessageSquare;
              }
              
              return (
                <motion.div
                  key={section.id}
                  variants={fadeIn("up", "spring", 0.7 + (index * 0.1), 0.75)}
                  initial="hidden"
                  animate="show"
                  className="w-full"
                >
                  <Card className="p-6 relative bg-transparent border border-gray-200 shadow-2xl backdrop-blur-sm rounded-xl mb-6">
                    <div className="absolute inset-0 -z-10 overflow-hidden rounded-xl">
                      <StarsBackground />
                    </div>
                    <div className="relative z-10">
                      <div className={`px-6 py-4 border-b ${performanceInfo ? performanceInfo.headerBorderColor : 'border-gray-300'} ${performanceInfo ? performanceInfo.headerBgColor : 'bg-transparent'}`}>
                        <h2 className="text-2xl font-bold text-left text-gray-900 flex items-center" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                          <SectionIcon className={`mr-2 ${performanceInfo ? performanceInfo.iconColor : 'text-blue-500'}`} size={24} />
                          {section.title}
                        </h2>
                      </div>
                      <div className="p-6 text-left text-gray-700 prose prose-slate max-w-none" style={{ fontFamily: '"Open Sans", sans-serif', fontSize: '16px' }}>
                        <ReactMarkdown components={MarkdownComponents}>
                          {cleanMarkdown(section.content)}
                        </ReactMarkdown>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })
          }
        </div>
      ) : (
        /* Fallback if no sections are identified */
        <motion.div
          variants={fadeIn("up", "spring", 0.3, 0.75)}
          initial="hidden"
          animate="show"
          className="w-full"
        >
          <Card className="p-6 relative bg-transparent border border-gray-200 shadow-2xl backdrop-blur-sm rounded-xl mb-6">
            <div className="absolute inset-0 -z-10 overflow-hidden rounded-xl">
              <StarsBackground />
            </div>
            <div className="relative z-10">
              <div className="px-6 py-4 border-b border-gray-300 bg-transparent" style={{ fontSize: '16px' }}>
                <h2 className="text-2xl font-bold text-left text-gray-900 flex items-center" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                  <FileText className="mr-2 text-blue-500" size={24} />
                  Interview Analysis
                </h2>
              </div>
              <div className="p-6 text-left text-gray-700 prose prose-slate max-w-none" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                <ReactMarkdown components={MarkdownComponents}>{cleanMarkdown(markdown)}</ReactMarkdown>
              </div>
            </div>
          </Card>
        </motion.div>
      )}
    </div>
  );
};

// Helper function to parse markdown into sections
function parseMarkdownSections(markdown: string) {
  const sections: {id: string; title: string; content: string}[] = [];
  
  // First check for the overall assessment section
  let overallContent = '';
  const overallMatch = markdown.match(/Overall Assessment:\s*([\s\S]*?)(?=\d+\.\s|$)/i);
  if (overallMatch) {
    overallContent = overallMatch[1].trim();
    sections.push({
      id: 'overall-assessment',
      title: 'Overall Assessment',
      content: overallContent
    });
  }
  
  // Now look for numbered sections (like "1. Overall Pitch Quality and Clarity:")
  const numberedSectionRegex = /(\d+)\.(\s+)([^:\n]+):(\s*[\s\S]*?)(?=\d+\.\s|$)/g;
  const numberedMatches = Array.from(markdown.matchAll(numberedSectionRegex));
  
  numberedMatches.forEach((match) => {
    const number = match[1];
    const title = match[3].trim();
    const content = match[4].trim();
    
    sections.push({
      id: `section-${number}`,
      title: `${number}. ${title}`,
      content: content
    });
  });
  
  // If no sections found, fall back to the original header-based approach
  if (sections.length === 0) {
    // Split by h1, h2, or h3 headers to catch more potential sections
    const headerRegex = /^(#{1,3})\s+(.+)$/gm;
    let lastIndex = 0;
    let lastTitle = '';
    let lastId = '';
    
    // Find all headers and their content
    const matches = Array.from(markdown.matchAll(headerRegex));
    
    if (matches.length === 0) {
      // If no headers found, return the whole content as one section
      return [{
        id: 'overview',
        title: 'Overview',
        content: markdown
      }];
    }
    
    matches.forEach((match, index) => {
      const [fullMatch, _, title] = match;
      const matchIndex = match.index as number;
      
      // If not the first match, add the previous section
      if (lastTitle) {
        const content = markdown.substring(lastIndex, matchIndex).trim();
        sections.push({
          id: lastId,
          title: lastTitle,
          content: content
        });
      }
      
      // Update for the next section
      lastIndex = matchIndex + fullMatch.length;
      lastTitle = title;
      lastId = title.toLowerCase().replace(/[^\w]+/g, '-');
      
      // If this is the last match, add the final section
      if (index === matches.length - 1) {
        const content = markdown.substring(lastIndex).trim();
        sections.push({
          id: lastId,
          title: lastTitle,
          content: content
        });
      }
    });
  }
  
  return sections;
}

export default InterviewAnalysis;
